import React, { useEffect, useState, useCallback } from "react";
import {
  Table,
  Card,
  Tag,
  Space,
  Typography,
  Input,
  Select,
  Row,
  Col,
  Button,
} from "antd";
import {
  SearchOutlined,
  ReloadOutlined,
  BankOutlined,
} from "@ant-design/icons";
import { getAdminFatouratiPayments } from "../../api/parkpass/parkpassFatouratiPayments";
import { getUserNames } from "../../api/parkpass/parkpassWalletTransactions";

const { Title } = Typography;
const { Option } = Select;

const statusColors = {
  pending: "orange",
  generated: "blue",
  paid: "green",
  failed: "red",
  expired: "default",
  cancelled: "default",
};

const statusLabels = {
  pending: "En attente",
  generated: "Généré",
  paid: "Payé",
  failed: "Échoué",
  expired: "Expiré",
  cancelled: "Annulé",
};

const productTypeLabels = {
  wallet_topup: "Recharge portefeuille",
  infraction: "Infraction",
};

const CitizenFatouratiPaymentsPage = () => {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 25,
    total: 0,
  });
  const [filters, setFilters] = useState({
    statusFilter: undefined,
    productType: undefined,
    userId: undefined,
    search: "",
  });

  const fetchPayments = useCallback(
    async (page = pagination.current, pageSize = pagination.pageSize, currentFilters = filters) => {
      try {
        setLoading(true);
        const response = await getAdminFatouratiPayments({
          page,
          pageSize,
          ...currentFilters,
        });
        setPayments(response.items || []);
        setPagination({
          current: response.page || 1,
          pageSize: response.page_size || pageSize,
          total: response.total || 0,
        });
      } catch (error) {
        console.error("Error fetching Fatourati payments:", error);
      } finally {
        setLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  useEffect(() => {
    fetchPayments(1, 25, filters);
    getUserNames()
      .then((data) => setUsers(data || []))
      .catch(console.error);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleTableChange = (newPagination) => {
    fetchPayments(newPagination.current, newPagination.pageSize, filters);
  };

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const handleSearch = () => {
    fetchPayments(1, pagination.pageSize, filters);
  };

  const handleReset = () => {
    const resetFilters = {
      statusFilter: undefined,
      productType: undefined,
      userId: undefined,
      search: "",
    };
    setFilters(resetFilters);
    fetchPayments(1, pagination.pageSize, resetFilters);
  };

  const columns = [
    {
      title: "ID",
      dataIndex: "payment_id",
      key: "payment_id",
      width: 70,
    },
    {
      title: "Utilisateur",
      key: "user",
      width: 200,
      render: (_, record) => (
        <div>
          <div style={{ fontWeight: 500 }}>
            {record.user_first_name || record.user_last_name
              ? `${record.user_first_name || ""} ${record.user_last_name || ""}`.trim()
              : "N/A"}
          </div>
          {record.user_email && (
            <div style={{ fontSize: 12, color: "#999" }}>{record.user_email}</div>
          )}
          <div style={{ fontSize: 11, color: "#bbb" }}>ID: {record.user_id}</div>
        </div>
      ),
    },
    {
      title: "Montant",
      dataIndex: "amount",
      key: "amount",
      width: 110,
      render: (amount) => (
        <span style={{ fontWeight: 600, color: "#52c41a" }}>
          {parseFloat(amount || 0).toFixed(2)} MAD
        </span>
      ),
    },
    {
      title: "Statut",
      dataIndex: "status",
      key: "status",
      width: 110,
      render: (s) => (
        <Tag color={statusColors[s] || "default"}>
          {statusLabels[s] || s}
        </Tag>
      ),
    },
    {
      title: "Type",
      dataIndex: "product_type",
      key: "product_type",
      width: 160,
      render: (type) => (
        <Tag color={type === "wallet_topup" ? "blue" : "volcano"}>
          {productTypeLabels[type] || type}
        </Tag>
      ),
    },
    {
      title: "Réf Fatourati",
      dataIndex: "ref_fatourati",
      key: "ref_fatourati",
      width: 180,
      render: (ref) => (
        <span style={{ fontFamily: "monospace", fontSize: 12 }}>{ref || "N/A"}</span>
      ),
    },
    {
      title: "Product ID",
      dataIndex: "product_id",
      key: "product_id",
      width: 120,
      render: (id) => id || "-",
    },
    {
      title: "Date de création",
      dataIndex: "created_at",
      key: "created_at",
      width: 160,
      render: (date) => (date ? new Date(date).toLocaleString("fr-FR") : "N/A"),
    },
    {
      title: "Expiration",
      dataIndex: "expiration_at",
      key: "expiration_at",
      width: 160,
      render: (date) => (date ? new Date(date).toLocaleString("fr-FR") : "-"),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Card
        title={
          <Space>
            <BankOutlined />
            <Title level={4} style={{ margin: 0 }}>
              Paiements Fatourati
            </Title>
          </Space>
        }
      >
        <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
          <Col xs={24} sm={12} md={5}>
            <Select
              placeholder="Statut"
              style={{ width: "100%" }}
              value={filters.statusFilter}
              onChange={(v) => handleFilterChange("statusFilter", v)}
              allowClear
            >
              <Option value="pending">En attente</Option>
              <Option value="generated">Généré</Option>
              <Option value="paid">Payé</Option>
              <Option value="failed">Échoué</Option>
              <Option value="expired">Expiré</Option>
            </Select>
          </Col>
          <Col xs={24} sm={12} md={4}>
            <Select
              placeholder="Type de produit"
              style={{ width: "100%" }}
              value={filters.productType}
              onChange={(v) => handleFilterChange("productType", v)}
              allowClear
            >
              <Option value="wallet_topup">Recharge</Option>
              <Option value="infraction">Infraction</Option>
            </Select>
          </Col>
          <Col xs={24} sm={12} md={5}>
            <Select
              placeholder="Utilisateur"
              style={{ width: "100%" }}
              value={filters.userId}
              onChange={(v) => handleFilterChange("userId", v)}
              allowClear
              showSearch
              optionFilterProp="children"
            >
              {users.map((u) => (
                <Option key={u.user_id} value={u.user_id}>
                  {u.first_name} {u.last_name}
                </Option>
              ))}
            </Select>
          </Col>
          <Col xs={24} sm={12} md={5}>
            <Input
              placeholder="Rechercher (Réf, UUID...)"
              prefix={<SearchOutlined />}
              value={filters.search}
              onChange={(e) => handleFilterChange("search", e.target.value)}
              onPressEnter={handleSearch}
              allowClear
            />
          </Col>
          <Col xs={24} sm={12} md={5}>
            <Space>
              <Button type="primary" icon={<SearchOutlined />} onClick={handleSearch}>
                Rechercher
              </Button>
              <Button icon={<ReloadOutlined />} onClick={handleReset}>
                Réinitialiser
              </Button>
            </Space>
          </Col>
        </Row>

        <Table
          columns={columns}
          dataSource={payments}
          rowKey="payment_id"
          loading={loading}
          pagination={{
            ...pagination,
            showSizeChanger: true,
            showTotal: (total) => `Total: ${total} paiements`,
            pageSizeOptions: ["10", "25", "50", "100"],
          }}
          onChange={handleTableChange}
          scroll={{ x: 1300 }}
        />
      </Card>
    </div>
  );
};

export default CitizenFatouratiPaymentsPage;
